import React, { useState, useEffect, useRef } from 'react';
import { Lock, X, ArrowRight, AlertCircle, Eye, EyeOff, ShieldCheck, Crown, Download } from 'lucide-react';
import { usePWAInstall } from '../utils/pwaInstall';
import { AdminInstallGuideModal } from './AdminInstallGuideModal';

export type AdminRole = 'super_admin' | 'admin';

interface AdminPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (role: AdminRole, pin: string) => void;
  requestedRole?: AdminRole;
}

const SUPER_ADMIN_PIN = (import.meta.env.VITE_SUPER_ADMIN_PIN || '7994').trim();
const ADMIN_PIN = (import.meta.env.VITE_ADMIN_PIN || '1234').trim();

export const AdminPinModal: React.FC<AdminPinModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  requestedRole
}) => {
  const [selectedRole, setSelectedRole] = useState<AdminRole>(requestedRole || 'super_admin');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const { isStandalone } = usePWAInstall();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (requestedRole) {
        setSelectedRole(requestedRole);
      }
      setPin('');
      setError('');
      setShowPin(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, requestedRole]);

  if (!isOpen) return null;

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(val);
    if (error) setError('');
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanPin = pin.trim();
    if (!cleanPin) {
      setError('Please enter your secret PIN');
      return;
    }

    // Smart authentication & role detection:
    // If entered PIN matches Super Admin PIN, grant Super Admin
    if (cleanPin === SUPER_ADMIN_PIN) {
      sessionStorage.setItem('blishka_admin_auth', 'true');
      sessionStorage.setItem('blishka_admin_pin', cleanPin);
      sessionStorage.setItem('blishka_admin_role', 'super_admin');
      localStorage.setItem('blishka_installed_portal', 'super-admin');
      setError('');
      onSuccess('super_admin', cleanPin);
      return;
    }

    // If entered PIN matches Operations Admin PIN, grant Operations Admin
    if (cleanPin === ADMIN_PIN) {
      if (selectedRole === 'super_admin') {
        setError('This PIN is for Operations Admin. Switch to Operations or enter Super Admin PIN (7994).');
        return;
      }
      sessionStorage.setItem('blishka_admin_auth', 'true');
      sessionStorage.setItem('blishka_admin_pin', cleanPin);
      sessionStorage.setItem('blishka_admin_role', 'admin');
      localStorage.setItem('blishka_installed_portal', 'admin');
      setError('');
      onSuccess('admin', cleanPin);
      return;
    }

    setError('Incorrect PIN. Access denied.');
    setPin('');
    inputRef.current?.focus();
  };

  const isSuper = selectedRole === 'super_admin';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="booking-modal-card admin-pin-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '440px',
          textAlign: 'center',
          padding: '2rem 1.75rem',
          border: isSuper ? '1.5px solid rgba(212, 175, 55, 0.4)' : '1px solid var(--border-soft)'
        }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close PIN prompt">
          <X size={20} />
        </button>

        {/* Role Segmented Selector */}
        <div
          style={{
            display: 'inline-flex',
            background: 'rgba(125, 26, 56, 0.06)',
            borderRadius: '999px',
            padding: '3px',
            marginBottom: '1.25rem',
            gap: '4px'
          }}
        >
          <button
            type="button"
            onClick={() => {
              setSelectedRole('admin');
              setError('');
              inputRef.current?.focus();
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              border: 'none',
              borderRadius: '999px',
              padding: '0.35rem 0.85rem',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: !isSuper ? '#ffffff' : 'transparent',
              color: !isSuper ? 'var(--blishka-berry)' : '#8b757b',
              boxShadow: !isSuper ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s ease'
            }}
          >
            <ShieldCheck size={13} />
            <span>Operations Admin</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedRole('super_admin');
              setError('');
              inputRef.current?.focus();
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              border: 'none',
              borderRadius: '999px',
              padding: '0.35rem 0.85rem',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: isSuper ? 'linear-gradient(135deg, #b8860b 0%, #d4af37 100%)' : 'transparent',
              color: isSuper ? '#ffffff' : '#8b757b',
              boxShadow: isSuper ? '0 2px 8px rgba(184, 134, 11, 0.3)' : 'none',
              transition: 'all 0.2s ease'
            }}
          >
            <Crown size={13} />
            <span>Super Admin (Finances)</span>
          </button>
        </div>

        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: isSuper
              ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(184, 134, 11, 0.25) 100%)'
              : 'rgba(125, 26, 56, 0.1)',
            color: isSuper ? '#b8860b' : 'var(--blishka-berry)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            boxShadow: isSuper ? '0 8px 24px rgba(184, 134, 11, 0.2)' : '0 8px 24px rgba(125, 26, 56, 0.12)'
          }}
        >
          {isSuper ? <Crown size={28} /> : <Lock size={26} />}
        </div>

        <h3
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '1.4rem',
            color: isSuper ? '#7d5a00' : 'var(--blishka-espresso, #2e1018)',
            marginBottom: '0.35rem'
          }}
        >
          {isSuper ? 'Super Admin Executive Console' : 'Operations Admin Console'}
        </h3>

        <p
          style={{
            fontSize: '0.85rem',
            color: 'var(--color-text-muted, #7c686e)',
            marginBottom: '1.5rem',
            lineHeight: 1.5
          }}
        >
          {isSuper
            ? 'Executive access: Total GMV, Cash in Hand, UPI Collections, Reconciliation & Financial Exports.'
            : 'Operational access: Live orders, staff dispatch & catalog. Financial metrics are hidden in this view.'}
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ position: 'relative', maxWidth: '240px', margin: '0 auto 1.25rem' }}>
            <input
              ref={inputRef}
              type={showPin ? 'text' : 'password'}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••"
              value={pin}
              onChange={handlePinChange}
              style={{
                width: '100%',
                fontSize: '1.75rem',
                letterSpacing: '0.35em',
                textAlign: 'center',
                padding: '0.65rem 2.5rem 0.65rem 1rem',
                borderRadius: '12px',
                border: error
                  ? '2px solid #d32f2f'
                  : isSuper
                  ? '2px solid rgba(212, 175, 55, 0.6)'
                  : '1.5px solid var(--border-soft, #e2d1d4)',
                background: '#ffffff',
                boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.04)',
                outline: 'none',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: isSuper ? '#b8860b' : 'var(--blishka-berry)'
              }}
            />
            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: '#9e858a',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
              title={showPin ? 'Hide PIN' : 'Show PIN'}
            >
              {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                color: '#d32f2f',
                fontSize: '0.84rem',
                fontWeight: 600,
                marginBottom: '1rem',
                animation: 'shake 0.3s ease-in-out'
              }}
            >
              <AlertCircle size={15} />
              <span>{error}</span>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button
              type="submit"
              className={isSuper ? 'btn' : 'btn btn-berry'}
              style={{
                width: '100%',
                padding: '0.8rem',
                fontSize: '0.95rem',
                background: isSuper ? 'linear-gradient(135deg, #b8860b 0%, #d4af37 100%)' : undefined,
                color: isSuper ? '#ffffff' : undefined,
                border: 'none'
              }}
            >
              <span>{isSuper ? 'Unlock Super Admin (Finances)' : 'Unlock Operations Console'}</span>
              <ArrowRight size={16} />
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8b757b',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '0.4rem'
              }}
            >
              Cancel & Return
            </button>
          </div>
        </form>

        <div
          style={{
            marginTop: '1.25rem',
            paddingTop: '0.85rem',
            borderTop: '1px solid rgba(125, 26, 56, 0.08)',
            fontSize: '0.75rem',
            color: '#a38d93'
          }}
        >
          Protected by Cloudflare Edge Security · Super Admin PIN: 7994 · Admin PIN: 1234
        </div>

        {/* Install Admin Dashboard as App */}
        {!isStandalone && (
          <div style={{ marginTop: '0.85rem' }}>
            <button
              type="button"
              onClick={() => setShowInstallModal(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                background: isSuper ? 'rgba(184, 134, 11, 0.08)' : 'rgba(125, 26, 56, 0.06)',
                border: isSuper ? '1px dashed rgba(184, 134, 11, 0.35)' : '1px dashed rgba(125, 26, 56, 0.25)',
                borderRadius: '8px',
                padding: '0.38rem 0.85rem',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: isSuper ? '#9a7000' : 'var(--blishka-berry)',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <Download size={13} />
              <span>Install {isSuper ? 'Super Admin' : 'Admin'} App on Desktop / Mobile</span>
            </button>
          </div>
        )}
      </div>

      <AdminInstallGuideModal
        isOpen={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        role={selectedRole}
      />
    </div>
  );
};
